import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';

interface Channel {
  id: number;
  channel_type: string;
  bank_name: string | null;
  account_name: string | null;
  account_no: string | null;
  chain_type: string | null;
  remark: string | null;
  exchange_rate: number | null;
}

const chainTypes = ['TRC20'];
const chainLabels: Record<string, string> = {
  TRC20: 'TRC20（推荐）',
};

function nowText() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function sanitizeFileName(file: File, index: number, timestamp: number): string {
  const ext = file.name.split('.').pop() || 'jpg';
  return `${timestamp}_${index}.${ext}`;
}

export default function UsdtDepositPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [selectedChain, setSelectedChain] = useState('TRC20');
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const inputAmount = parseFloat(amount) || 0;
  const activeChannel = channels.find((c) => c.chain_type === selectedChain)
    || channels.find((c) => !c.chain_type && c.channel_type === 'USDT');
  const exchangeRate = activeChannel?.exchange_rate || 0;
  // 用户输入的是 USDT 数量，自动折算人民币金额
  const rmbAmount = exchangeRate > 0 ? Number((inputAmount * exchangeRate).toFixed(2)) : 0;
  // 最低入金 100 人民币对应的 USDT 数量
  const minUsdt = exchangeRate > 0 ? Number((100 / exchangeRate).toFixed(2)) : 0;
  const minRmb = 100;

  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    try {
      const { data, error } = await supabase
        .from('deposit_channels')
        .select('id, channel_type, bank_name, account_name, account_no, chain_type, remark, exchange_rate')
        .eq('enabled', true)
        .eq('channel_type', 'USDT')
        .order('sort', { ascending: true })
        .order('id', { ascending: true });
      if (error) throw error;
      setChannels((data || []) as Channel[]);
    } catch {
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,4}$/.test(val)) {
      setAmount(val);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('已复制到剪贴板', 'success');
    } catch {
      showToast('复制失败，请手动复制', 'error');
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      showToast('登录已失效，请重新登录', 'error');
      return;
    }

    setUploading(true);
    const newUrls: string[] = [];
    const timestamp = Date.now();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        showToast('仅支持上传图片文件', 'error');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        showToast('单张图片不能超过 10MB', 'error');
        continue;
      }

      const fileName = sanitizeFileName(file, i, timestamp);
      const path = `deposits/usdt/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage.from('public').upload(path, file, {
        upsert: true,
        contentType: file.type,
      });

      if (uploadError) {
        console.error('[USDT Upload] error:', uploadError);
        showToast(`上传失败: ${uploadError.message}`, 'error');
        continue;
      }

      const { data: publicUrlData } = supabase.storage.from('public').getPublicUrl(path);
      newUrls.push(publicUrlData.publicUrl);
    }

    if (newUrls.length > 0) {
      setScreenshots((prev) => [...prev, ...newUrls]);
      showToast(`已上传 ${newUrls.length} 张截图`, 'success');
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveScreenshot = (index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const canSubmit = () => {
    const validAmount = exchangeRate > 0 ? rmbAmount >= minRmb : inputAmount > 0;
    return validAmount && !!activeChannel && walletAddress.trim().length >= 10;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('登录已失效');

      const depositAmount = exchangeRate > 0 ? rmbAmount : inputAmount;

      const { error } = await supabase.from('deposits').insert({
        user_id: user.id,
        amount: depositAmount,
        channel: 'USDT',
        channel_id: activeChannel?.id ?? null,
        account: walletAddress.trim(),
        payer_name: null,
        bank_name: null,
        remark: selectedChain,
        status: 'pending',
        create_time: nowText(),
        screenshots: screenshots.length > 0 ? screenshots : null,
      });

      if (error) throw error;

      // 通知管理员（不阻塞提交）
      supabase.functions.invoke('send-deposit-email', {
        body: {
          userId: user.id,
          type: 'USDT',
          amount: depositAmount,
          chainType: selectedChain,
          walletAddress: walletAddress.trim(),
          createTime: nowText(),
        },
      }).catch(() => {});

      setSubmitted(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '提交失败', 'error');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brown-50 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <i className="ri-check-line text-green-500 text-4xl"></i>
        </div>
        <h2 className="text-brown-900 text-lg font-bold mb-2">入金申请已提交</h2>
        <p className="text-brown-500 text-sm text-center">
          {exchangeRate > 0 && inputAmount > 0 ? (
            <>
              <span className="text-brown-900 font-bold">{inputAmount} USDT</span>
              <span className="block text-brown-400 text-xs mt-1">
                ≈ ¥{rmbAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}（汇率 ¥{exchangeRate.toFixed(2)}/USDT）
              </span>
            </>
          ) : (
            <span className="text-brown-900 font-bold">{inputAmount} USDT</span>
          )}
          <span className="block text-brown-400 text-xs mt-1">{selectedChain} 链</span>
        </p>
        <p className="text-brown-400 text-xs mt-2">后台审核通过后将为您入账</p>
        <button
          onClick={() => {
            setSubmitted(false);
            setAmount('');
            setWalletAddress('');
            setScreenshots([]);
          }}
          className="mt-6 px-6 py-2.5 rounded-full bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          继续入金
        </button>
        <button
          onClick={() => navigate('/profile/transactions')}
          className="mt-3 text-gold-500 text-sm cursor-pointer"
        >
          查看入金记录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brown-50 pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">USDT 入金</span>
        <div className="w-8" />
      </div>

      {/* USDT标志 */}
      <div className="flex flex-col items-center pt-6 pb-4">
        <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-2">
          <span className="text-white text-2xl font-bold tracking-tight">T</span>
        </div>
        <h2 className="text-brown-900 text-lg font-bold">USDT 入金</h2>
        <p className="text-brown-500 text-xs mt-1">推荐使用 · 安全便捷</p>
      </div>

      <div className="px-4 space-y-4">
        {/* 公链类型 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-600 text-sm mb-3">公链类型</label>
          <div className="inline-flex px-4 py-2 rounded-lg text-sm font-medium bg-gold-500 text-brown-950 border border-gold-500">
            TRC20（推荐）
          </div>
        </div>

        {/* 收款地址 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-600 text-sm mb-3">收款地址</label>
          {channelsLoading ? (
            <div className="flex items-center justify-center py-6">
              <span className="w-5 h-5 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
            </div>
          ) : activeChannel?.account_no ? (
            <div className="bg-brown-50 rounded-lg p-3 border border-brown-100">
              <p className="text-brown-900 text-sm font-mono break-all leading-relaxed">
                {activeChannel.account_no}
              </p>
              <button
                onClick={() => handleCopy(activeChannel.account_no || '')}
                className="mt-2 w-full py-2 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer flex items-center justify-center gap-1"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-file-copy-line text-sm"></i>
                </span>
                复制地址
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-brown-400 text-sm">该公链暂无收款地址</p>
              <p className="text-brown-400 text-xs mt-1">请选择其他公链或联系客服</p>
            </div>
          )}

          {activeChannel?.remark && (
            <p className="text-brown-400 text-xs mt-2">{activeChannel.remark}</p>
          )}
        </div>

        {/* 汇率信息 */}
        {exchangeRate > 0 && (
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-amber-500 flex-shrink-0">
                <i className="ri-exchange-line text-sm"></i>
              </span>
              <span className="text-amber-800 text-xs font-medium">当前汇率</span>
            </div>
            <span className="text-amber-900 text-sm font-bold">
              1 USDT = ¥{exchangeRate.toFixed(2)}
            </span>
          </div>
        )}

        {/* 汇率未设置提示 */}
        {!!activeChannel && exchangeRate <= 0 && !channelsLoading && (
          <div className="bg-red-50 rounded-xl p-3 border border-red-200 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
              <i className="ri-alert-line text-sm"></i>
            </span>
            <p className="text-red-700 text-xs">汇率未设置，请联系管理员在后台配置 USDT 汇率</p>
          </div>
        )}

        {/* 转账金额 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-600 text-sm mb-2">USDT 转账数量</label>
          <div className="flex items-center gap-2 border-b-2 border-gold-200 pb-2">
            <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">T</span>
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              placeholder="输入 USDT 数量"
              className="flex-1 text-brown-900 text-xl font-bold outline-none bg-transparent"
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            {exchangeRate > 0 && minUsdt > 0 ? (
              <p className="text-brown-400 text-xs">最低入金约 {minUsdt} USDT</p>
            ) : (
              <p className="text-brown-400 text-xs">最低入金 ¥{minRmb}</p>
            )}
            {exchangeRate > 0 && inputAmount > 0 && (
              <p className="text-amber-700 text-xs font-semibold">
                ≈ ¥{rmbAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} 人民币
              </p>
            )}
          </div>
          {exchangeRate > 0 && minUsdt > 0 && (
            <p className="text-brown-400 text-xs mt-0.5">
              约 ¥{minRmb} 人民币
            </p>
          )}
        </div>

        {/* 您的钱包地址 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-600 text-sm mb-2">您的钱包地址</label>
          <input
            type="text"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            placeholder="请填写您的 USDT 钱包地址"
            className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
          />
          <p className="text-brown-400 text-xs mt-2">请确保地址正确，否则资金将无法找回</p>
        </div>

        {/* 转账截图上传 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-brown-600 text-sm">转账截图</label>
            <span className="text-brown-400 text-xs">{screenshots.length > 0 ? `已上传 ${screenshots.length} 张` : '可选'}</span>
          </div>
          <p className="text-brown-400 text-xs mb-3">上传 USDT 转账成功的截图，方便后台快速审核</p>

          {/* 已上传截图预览 */}
          {screenshots.length > 0 && (
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {screenshots.map((url, idx) => (
                <div key={idx} className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-brown-200">
                  <img src={url} alt={`截图${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleRemoveScreenshot(idx)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full py-3 rounded-xl border-2 border-dashed border-brown-200 text-brown-500 text-sm font-medium cursor-pointer hover:border-gold-400 hover:bg-gold-50/50 transition-colors flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <span className="w-4 h-4 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
                上传中...
              </>
            ) : (
              <>
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-camera-line text-lg"></i>
                </span>
                点击上传转账截图
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <p className="text-brown-400 text-xs mt-2">支持 JPG、PNG 格式，单张不超过 10MB</p>
        </div>

        {/* 提示 */}
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-amber-500 mt-0.5 flex-shrink-0">
              <i className="ri-information-line text-sm"></i>
            </span>
            <div>
              <p className="text-brown-600 text-xs">
                转账时请仔细核对收款地址和公链类型，确认无误后再发起转账。
              </p>
              <p className="text-brown-500 text-xs mt-1">
                转账完成后，请提交申请，我们将在链上确认后为您入账。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 提交按钮 */}
      <div className="px-4 mt-6 mb-8">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit() || submitting}
          className={`w-full py-3.5 rounded-xl text-white text-sm font-semibold cursor-pointer transition-all ${
            canSubmit() && !submitting
              ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98] shadow-sm shadow-amber-500/20'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              提交中...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-download-line"></i>
              确认入金
            </span>
          )}
        </button>
        <p className="text-brown-400 text-xs text-center mt-3">
          入金申请提交后，后台管理员审核通过将自动入账
        </p>
      </div>
    </div>
  );
}